// LISA — "Lucid Architect" redesign tokens
// Editorial light palette · depth from tonal stacking, not borders.

// ── Raw tokens (source of truth) ───────────────────────────────────────
const tokens = {
  // Surfaces — canvas → card → inner card
  surface:                '#f1f3f6', // page canvas
  surfaceLow:             '#eaeef2', // nested blocks, inner cards
  surfaceLowest:          '#ffffff', // elevated cards, panels
  surfaceContainer:       '#eaeef2',
  surfaceContainerHigh:   '#e2e9ee',

  // Text
  onSurface:              '#2b3438',
  onSurfaceVariant:       '#586065',
  outlineVariant:         '#aab3b9',

  // Primary — LISA blue
  primary:                '#005ac4',
  primaryDim:             '#004eac',
  primaryContainer:       '#d8e2ff',
  onPrimaryContainer:     '#004eab',

  // Semantic
  tertiary:               '#bb1b21',
  errorContainer:         '#fde1df',
  onErrorContainer:       '#752121',
  success:                '#2f6b5a',
  successContainer:       '#d5ece2',
  warn:                   '#8b5e0e',
  warnBg:                 '#fcefd6',
  purple:                 '#5b46b8',
  purpleBg:               '#ece7fb',
};

// Signature gradient — primary → primary-dim @ 135° — used on CTAs and brand mark
export const gradient = {
  primary: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDim} 100%)`,
  verdict: `linear-gradient(135deg, #1a0406 0%, #2e0a0d 50%, #3d0e11 100%)`,
  insight: `linear-gradient(135deg, #0a2a5a 0%, #004eac 100%)`,
};

// ── Public palette (API — used throughout the app) ─────────────────────
export const colors = {
  // Surfaces
  bg:               tokens.surface,
  surface:          tokens.surfaceLowest,  // elevated card background
  surfaceElevated:  tokens.surfaceLow,     // nested / alt rows
  surfaceHover:     tokens.surfaceContainerHigh,
  surfaceNested:    tokens.surfaceLow,
  surfaceInput:     tokens.surfaceLowest,

  // Raw surface aliases (prototype parity)
  canvas:           tokens.surface,
  surfaceLow:       tokens.surfaceLow,
  surfaceLowest:    tokens.surfaceLowest,
  surfaceContainer: tokens.surfaceContainer,

  // Borders — mostly ghost; use outlineVariant sparingly
  border:           'rgba(43, 52, 56, 0.08)',
  borderSubtle:     'rgba(43, 52, 56, 0.04)',
  outlineVariant:   tokens.outlineVariant,

  // Accent / primary brand blue
  accent:           tokens.primary,
  accentHover:      tokens.primaryDim,
  accentDim:        'rgba(0, 90, 196, 0.10)',
  accentGlow:       '0 0 24px rgba(0, 90, 196, 0.18)',
  accentGradient:   gradient.primary,
  primary:          tokens.primary,
  primaryDim:       tokens.primaryDim,
  primaryContainer: tokens.primaryContainer,
  onPrimaryContainer: tokens.onPrimaryContainer,

  // Text
  textPrimary:      tokens.onSurface,
  textSecondary:    tokens.onSurfaceVariant,
  textMuted:        tokens.outlineVariant,
  textOnAccent:     '#ffffff',

  // Risk levels — map to prototype semantic colors
  critical:         tokens.tertiary,
  criticalBg:       tokens.errorContainer,
  criticalBorder:   'rgba(187, 27, 33, 0.20)',
  criticalOn:       tokens.onErrorContainer,

  high:             tokens.warn,
  highBg:           tokens.warnBg,
  highBorder:       'rgba(139, 94, 14, 0.22)',

  medium:           tokens.primary,
  mediumBg:         tokens.primaryContainer,
  mediumBorder:     'rgba(0, 90, 196, 0.22)',

  low:              tokens.success,
  lowBg:            tokens.successContainer,
  lowBorder:        'rgba(47, 107, 90, 0.22)',

  // Extras
  purple:           tokens.purple,
  purpleBg:         tokens.purpleBg,
  tertiary:         tokens.tertiary,
  errorContainer:   tokens.errorContainer,
  onErrorContainer: tokens.onErrorContainer,
  success:          tokens.success,
  successContainer: tokens.successContainer,
  warn:             tokens.warn,
  warnBg:           tokens.warnBg,

  // MUI theme secondary — aliased to purple accent so legacy theme.js works
  secondary:        tokens.purple,
  secondaryBg:      tokens.purpleBg,

  // Status
  online:           tokens.success,
  warning:          tokens.warn,
  error:            tokens.tertiary,

  // Avatar palette — soft, on-surface-friendly
  avatar: [
    'linear-gradient(135deg, #2b7de9, #1e5ac4)',
    'linear-gradient(135deg, #8a5cf6, #6c3dd0)',
    'linear-gradient(135deg, #e06d85, #b84a66)',
    'linear-gradient(135deg, #d48e28, #a66911)',
    'linear-gradient(135deg, #2ea37c, #1f7859)',
    'linear-gradient(135deg, #e05c4f, #b8362a)',
    'linear-gradient(135deg, #2b7de9, #1a54a3)',
  ],
};

// ── Shadows — never heavy black, always tinted by on-surface ──────────
export const shadows = {
  card:     '0 1px 2px rgba(43, 52, 56, 0.04), 0 8px 24px rgba(43, 52, 56, 0.04)',
  ambient:  '0 1px 2px rgba(43, 52, 56, 0.04), 0 8px 24px rgba(43, 52, 56, 0.04)',
  lift:     '0 2px 4px rgba(43, 52, 56, 0.05), 0 18px 40px rgba(43, 52, 56, 0.06)',
  elevated: '0 2px 6px rgba(43, 52, 56, 0.08), 0 16px 36px rgba(43, 52, 56, 0.14)',
  glow:     '0 0 30px rgba(0, 90, 196, 0.14)',
  button:   '0 4px 14px rgba(0, 90, 196, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
  panel:    '-18px 0 40px rgba(43, 52, 56, 0.08), -2px 0 4px rgba(43, 52, 56, 0.04)',
};

// ── Typography ────────────────────────────────────────────────────────
export const typography = {
  fontFamily:        "'Inter', system-ui, sans-serif",
  fontFamilyDisplay: "'Manrope', sans-serif",
  fontFamilySerif:   "'Source Serif 4', Georgia, serif",
  fontFamilyMono:    "'JetBrains Mono', monospace",
};

// ── Radius ────────────────────────────────────────────────────────────
export const radius = {
  sm: '6px',   // chips, pills, small buttons
  md: '8px',   // buttons, inputs, inner cards
  lg: '12px',  // panels, prominent cards
  xl: '16px',  // hero cards, main surfaces
};

// ── Reusable sx objects ───────────────────────────────────────────────

export const cardSx = {
  bgcolor:      colors.surface,
  borderRadius: radius.xl,
  boxShadow:    shadows.card,
};

export const cardNestedSx = {
  bgcolor:      colors.surfaceElevated,
  borderRadius: radius.lg,
  boxShadow:    'none',
};

export const buttonPrimarySx = {
  background:   gradient.primary,
  color:        colors.textOnAccent,
  fontFamily:   typography.fontFamily,
  fontWeight:   600,
  fontSize:     '13px',
  letterSpacing: '0.01em',
  textTransform: 'none',
  borderRadius: radius.md,
  px:           2,
  py:           1.1,
  boxShadow:    shadows.button,
  transition:   'filter 0.15s, transform 0.08s',
  '&:hover':    { background: gradient.primary, filter: 'brightness(1.05)' },
  '&:active':   { transform: 'translateY(1px)' },
  '&.Mui-disabled': { background: colors.surfaceElevated, color: colors.textMuted, boxShadow: 'none' },
};

export const buttonSecondarySx = {
  background:   colors.surface,
  color:        colors.textPrimary,
  fontFamily:   typography.fontFamily,
  fontWeight:   600,
  fontSize:     '13px',
  textTransform: 'none',
  borderRadius: radius.md,
  px:           2,
  py:           1.1,
  border:       0,
  boxShadow:    shadows.card,
  '&:hover':    { background: colors.primaryContainer, color: colors.onPrimaryContainer },
};

export const chipSx = {
  bgcolor:      colors.primaryContainer,
  color:        colors.onPrimaryContainer,
  fontFamily:   typography.fontFamily,
  fontWeight:   600,
  fontSize:     '11px',
  letterSpacing: '0.02em',
  borderRadius: '999px',
  height:       24,
  border:       'none',
};

/** Meta label kicker (uppercase, mono or display, ~10–11px) */
export const labelSx = {
  fontFamily:    typography.fontFamily,
  fontSize:      '10.5px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color:         colors.textSecondary,
  fontWeight:    600,
};

/** DataGrid — light, tonal rows (back-compat export name) */
export const dataGridDarkSx = {
  fontFamily: typography.fontFamily,
  fontSize:   '12px',
  color:      colors.textPrimary,
  border:     'none',
  borderRadius: radius.lg,
  backgroundColor: colors.surface,

  '& .MuiDataGrid-main':            { backgroundColor: colors.surface },
  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.surface },
  '& .MuiDataGrid-filler':          { backgroundColor: colors.surface },
  '& .MuiDataGrid-scrollbarFiller': { backgroundColor: colors.surfaceElevated },

  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.surfaceElevated, borderBottom: 'none' },
  '& .MuiDataGrid-columnHeader':  { backgroundColor: colors.surfaceElevated },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: colors.textSecondary,
    fontFamily: typography.fontFamilyDisplay,
    fontWeight: 700,
    fontSize: '10px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-columnSeparator': { color: 'transparent' },
  '& .MuiDataGrid-sortIcon': { color: colors.accent },
  '& .MuiDataGrid-menuIcon svg': { color: colors.textSecondary },
  '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': { color: colors.textSecondary },

  '& .MuiDataGrid-row': {
    backgroundColor: colors.surface,
    transition: 'background 0.15s',
    '&:hover': { backgroundColor: colors.surfaceElevated },
    '&.Mui-hovered': { backgroundColor: colors.surfaceElevated },
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    backgroundColor: 'rgba(234, 238, 242, 0.5)',
    '&:hover': { backgroundColor: colors.surfaceElevated },
  },

  '& .MuiDataGrid-cell': {
    color: colors.textPrimary,
    borderBottom: 'none',
    borderRight: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },

  '& .MuiDataGrid-footerContainer': {
    backgroundColor: colors.surfaceElevated,
    borderTop: 'none',
    color: colors.textSecondary,
  },
  '& .MuiTablePagination-root': { color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '12px' },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontFamily: typography.fontFamily, fontSize: '11px', color: colors.textSecondary,
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: colors.textSecondary,
    '&:hover': { color: colors.accent },
    '&.Mui-disabled': { color: colors.textMuted },
  },
  '& .MuiSelect-select': { color: colors.textSecondary, fontFamily: typography.fontFamily },
  '& .MuiDataGrid-overlay': { backgroundColor: colors.surface, color: colors.textSecondary },
  '& .wrap-header .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal', lineHeight: '15px', textAlign: 'center', display: 'block',
  },
};

/** TextField — light tonal surface, focus glow in primary */
export const darkTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceElevated,
    color: colors.textPrimary,
    fontFamily: typography.fontFamily,
    fontSize: '13px',
    borderRadius: radius.md,
    transition: 'background-color 0.15s, box-shadow 0.15s',
    '& fieldset':             { borderColor: 'transparent' },
    '&:hover fieldset':       { borderColor: 'transparent' },
    '&.Mui-focused':          { backgroundColor: colors.surface, boxShadow: `0 0 0 2px ${colors.primaryContainer}` },
    '&.Mui-focused fieldset': { borderColor: 'transparent' },
  },
  '& .MuiInputBase-input': {
    color: colors.textPrimary,
    '&::placeholder': { color: colors.textMuted, opacity: 1 },
  },
  '& .MuiInputAdornment-root svg': { color: colors.textMuted },
};

// Forward-looking aliases
export const dataGridSx = dataGridDarkSx;
export const textFieldSx = darkTextFieldSx;
